import React, { useState, useRef } from 'react';
import { Camera, Upload, Loader2, TrendingUp, BarChart3, Target } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { GeminiService } from '../services/geminiService';
import { ScoreBar } from './ScoreBar';


export default function TennisServeAnalyzer() {
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      setVideoUrl(URL.createObjectURL(file));
      setCurrentAnalysis(null);
      setError(null);
    }
  };

  const analyzeServe = async () => {
    if (!videoFile || !videoRef.current) return;

    setAnalyzing(true);
    setError(null);

    try {
      const analysis = await GeminiService.analyzeVideo(videoFile);

      const serveData = {
        ...analysis,
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleDateString(),
        videoName: videoFile.name
      };

      setCurrentAnalysis(serveData);
      setHistory(prev => [...prev, serveData]);
    } catch (err) {
      console.error("Error analyzing video:", err);
      setError("Failed to analyze the video. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const resetAnalysis = () => {
    setVideoFile(null);
    setVideoUrl(null);
    setCurrentAnalysis(null);
    setError(null);
  };

  const progressData = history.map((entry, idx) => ({
    session: `#${idx + 1}`,
    overall: entry.overallScore,
    stance: entry.scores.stance,
    toss: entry.scores.toss,
    power: entry.scores.power
  }));

  const radarData = currentAnalysis ? [
    { aspect: 'Stance', score: currentAnalysis.scores.stance },
    { aspect: 'Toss', score: currentAnalysis.scores.toss },
    { aspect: 'Backswing', score: currentAnalysis.scores.backswing },
    { aspect: 'Contact', score: currentAnalysis.scores.contact },
    { aspect: 'Power', score: currentAnalysis.scores.power },
    { aspect: 'Follow-Through', score: currentAnalysis.scores.followThrough }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Target className="w-10 h-10 text-green-600" />
            <h1 className="text-4xl font-bold text-gray-800">Tennis Serve Analyzer Pro</h1>
          </div>
          <p className="text-gray-600 text-lg">
            AI-Powered Serve Analysis with Performance Tracking
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upload & Analyze</h2>

            {!videoUrl ? (
              <div
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className="border-3 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-2">Click to upload your serve video</p>
                <p className="text-sm text-gray-400">MP4, MOV, or WebM</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  className="w-full max-h-96 rounded-lg mb-4 object-contain"
                />
                <div className="flex gap-3">
                  <button
                    onClick={analyzeServe}
                    disabled={analyzing}
                    className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        Analyze Serve
                      </>
                    )}
                  </button>
                  <button
                    onClick={resetAnalysis}
                    className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Quick Stats
            </h2>

            {currentAnalysis ? (
              <div className="space-y-4">
                <div className="text-center p-6 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg text-white">
                  <div className="text-5xl font-bold mb-2">{currentAnalysis.overallScore}</div>
                  <div className="text-sm uppercase tracking-wide">Overall Score</div>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{currentAnalysis.velocityEstimate}</div>
                  <div className="text-sm text-gray-600">Estimated Velocity (mph)</div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium">Sessions</span>
                    <span className="text-lg font-bold text-green-600">{history.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm font-medium">Avg Score</span>
                    <span className="text-lg font-bold text-purple-600">
                      {history.length > 0 ? (history.reduce((sum, h) => sum + h.overallScore, 0) / history.length).toFixed(1) : '—'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Target className="w-16 h-16 mx-auto mb-4" />
                <p className="text-sm">Upload and analyze a video to see your stats</p>
              </div>
            )}
          </div>
        </div>

        {currentAnalysis && (
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Technique Breakdown</h2>

              <ScoreBar label="Stance & Balance" score={currentAnalysis.scores.stance} />
              <ScoreBar label="Toss Technique" score={currentAnalysis.scores.toss} />
              <ScoreBar label="Backswing & Prep" score={currentAnalysis.scores.backswing} />
              <ScoreBar label="Contact Point" score={currentAnalysis.scores.contact} />
              <ScoreBar label="Power Generation" score={currentAnalysis.scores.power} />
              <ScoreBar label="Follow-Through" score={currentAnalysis.scores.followThrough} />

              <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">💪 Strengths</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {currentAnalysis.strengths.map((s, i) => (
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">🎯 Focus Areas</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {currentAnalysis.improvements.map((imp, i) => (
                    <li key={i}>• {imp}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Performance Radar</h2>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="aspect" />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} />
                  <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                </RadarChart>
              </ResponsiveContainer>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Detailed Feedback</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{currentAnalysis.detailedFeedback}</p>
              </div>
            </div>
          </div>
        )}

        {history.length > 1 && (
          <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Progress Tracking
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="session" />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="overall" stroke="#8b5cf6" strokeWidth={3} name="Overall Score" />
                <Line type="monotone" dataKey="stance" stroke="#10b981" strokeWidth={2} name="Stance" />
                <Line type="monotone" dataKey="toss" stroke="#3b82f6" strokeWidth={2} name="Toss" />
                <Line type="monotone" dataKey="power" stroke="#f59e0b" strokeWidth={2} name="Power" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}